import React, { useEffect } from 'react';
import { useSelectInput, SelectOption } from './hooks/useSelectInput';
import { catalogClient } from '../services/grpc';
import { ModelReq } from '../generated/catalog_pb';
import '../styles/select-input.css';

const loadModels = async (
  query: string,
  markId: number
): Promise<SelectOption[]> => {
  const req = new ModelReq();
  req.setName(query);
  req.setAutoId(markId);

  return new Promise((resolve, reject) => {
    catalogClient.modelRequest(req, {}, (err, response) => {
      if (err) {
        reject(err);
        return;
      }

      const items =
        response?.getListList().map((item) => ({
          id: item.getModelId(),
          label: item.getName(),
        })) || [];

      resolve(items);
    });
  });
};

interface ModelSelectProps {
  markId: number | null;
  onModelSelect: (model: SelectOption | null) => void;
  inputId?: string;
  placeholder?: string;
  disabled?: boolean;
  disabledPlaceholder?: string;
}

const ModelSelect: React.FC<ModelSelectProps> = ({
  markId,
  onModelSelect,
  inputId,
  placeholder = 'Выберите модель...',
  disabled,
  disabledPlaceholder = 'Сначала выберите марку',
}) => {
  const isDisabled = disabled || !markId;

  const {
    inputValue,
    setInputValue,
    selectedOption,
    options,
    isLoading,
    isOpen,
    handleSelect,
    handleBlur,
    openDropdown,
    reset,
    loadCurrentOptions,
  } = useSelectInput<SelectOption>({
    loadOptions: (query) => {
      if (!markId) {
        return Promise.resolve([]);
      }

      return loadModels(query, markId);
    },
    debounceMs: 300,
    minQueryLength: 0,
  });

  useEffect(() => {
    onModelSelect(selectedOption);
  }, [selectedOption, onModelSelect]);

  useEffect(() => {
    reset();
  }, [markId, reset]);

  useEffect(() => {
    if (isDisabled) {
      reset();
    }
  }, [isDisabled, reset]);

  const placeholderText = isDisabled ? disabledPlaceholder : placeholder;
  const shouldOpenOnFocus = !isDisabled && options.length > 0;

  return (
    <div className="select-input" onBlur={handleBlur}>
      <input
        id={inputId}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={placeholderText}
        onFocus={() => {
          if (isDisabled) {
            return;
          }

          if (inputValue === '') {
            if (options.length === 0) {
              loadCurrentOptions();
            } else {
              openDropdown();
            }
          } else if (shouldOpenOnFocus) {
            openDropdown();
          }
        }}
        className="select-input__field"
        autoComplete="off"
        disabled={isDisabled}
      />

      {isOpen && !isDisabled && isLoading && (
        <div className="select-input__status select-input__status--loading">
          Загрузка...
        </div>
      )}

      {isOpen && !isDisabled && !isLoading && options.length > 0 && (
        <ul className="select-input__dropdown">
          {options.map((option) => (
            <li
              key={option.id}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => handleSelect(option)}
              className="select-input__option"
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}

      {isOpen && !isDisabled && !isLoading && options.length === 0 && (
        <div className="select-input__status">Ничего не найдено</div>
      )}
    </div>
  );
};

export default ModelSelect;